import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FileText, Download, CheckCircle2, Info } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface BriefingViewerProps {
  briefing: string;
  expertName: string;
  journalistName: string;
}

export function BriefingViewer({ briefing, expertName, journalistName }: BriefingViewerProps) {
  const handleDownload = () => {
    const blob = new Blob([briefing], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interview-briefing-${expertName.replace(/\s+/g, '-').toLowerCase()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Interview Briefing</h3>
          <p className="text-sm text-muted-foreground">
            Review the complete briefing document for the expert
          </p>
        </div>
        <Button variant="outline" onClick={handleDownload}>
          <Download className="w-4 h-4 mr-2" />
          Download Briefing
        </Button>
      </div>

      {/* Success Alert */}
      <Alert className="bg-green-50 border-green-200">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          <strong>Interview Setup Complete!</strong> The briefing document has been generated
          and is ready to be sent to the expert.
        </AlertDescription>
      </Alert>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          This briefing will be sent to {expertName} to help them prepare for the interview.
          It includes the interview goals, questions, preparation tips, and technical setup guide.
        </AlertDescription>
      </Alert>

      {/* Briefing Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Briefing Document
          </CardTitle>
          <CardDescription>
            This is what the expert will receive
          </CardDescription>
        </CardHeader>
        <CardContent>
          {briefing ? (
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown>{briefing}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No briefing content available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Click "Finish & Create Interview" to save the interview</li>
            <li>Use "Send Invite" button to email the briefing to the expert</li>
            <li>Expert will receive a secure link to access the preparation flow</li>
            <li>Expert completes the 4-screen preparation process</li>
            <li>Expert records the interview when ready</li>
            <li>Review the transcript and generate SEO content</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
